import { browser } from "$app/env";

/**
 * localStorage key for in-progress note drafts. Mirrors the other client-only
 * caches in this app (`recentlyViewedCaseFiles`, `authState`).
 */
const STORAGE_KEY = "caseFileNoteDrafts";

/**
 * Bucket used when no user is available (a stale page during logout). Drafts
 * are namespaced per user so a shared browser never shows one account's note
 * to another.
 */
export const ANONYMOUS_USER_ID = "anonymous";

/** A note the user typed but has not submitted to the server yet. */
export interface NoteDraft {
	content: string;
	updated_at: number;
}

type UserDrafts = Record<string, NoteDraft>;
type DraftMap = Record<string, UserDrafts>;

function readStoredDrafts(): DraftMap {
	if (!browser) return {};
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return {};
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
		return parsed as DraftMap;
	} catch {
		// Corrupt or unavailable storage — start empty instead of failing the page.
		return {};
	}
}

/**
 * In-progress "add note" text, persisted client-side in localStorage.
 *
 * The note composer is a modal, so a draft has to outlive closing the modal,
 * refreshing the tab, and navigating away from the case file. Each draft is
 * keyed by user and case file: different accounts on the same browser never
 * inherit somebody else's note, and each case file resumes its own draft.
 * Drafts are local only — nothing is sent to the server until "Add Note".
 */
class NoteDraftsStore {
	drafts = $state<DraftMap>(readStoredDrafts());

	/** The saved draft for a user's case file, if there is one. */
	draft(userId: string, caseFileId: string): NoteDraft | undefined {
		if (!userId || !caseFileId) return undefined;
		return this.drafts[userId]?.[caseFileId];
	}

	/** Draft text, or an empty string when there is no draft. */
	content(userId: string, caseFileId: string): string {
		return this.draft(userId, caseFileId)?.content ?? "";
	}

	/** True when a non-blank draft exists; drives the unsaved-note indicators. */
	hasDraft(userId: string, caseFileId: string): boolean {
		const draft = this.draft(userId, caseFileId);
		return !!draft?.content.trim();
	}

	/**
	 * Saves the draft. Called on every keystroke so a refresh or navigation can
	 * never drop more than the most recent edit. Blank content deletes it.
	 */
	save(userId: string, caseFileId: string, content: string): void {
		if (!userId || !caseFileId) return;
		if (!content.trim()) {
			this.clear(userId, caseFileId);
			return;
		}
		const next: DraftMap = {
			...this.drafts,
			[userId]: {
				...this.drafts[userId],
				[caseFileId]: { content, updated_at: Date.now() },
			},
		};
		this.#commit(next);
	}

	/** Deletes one draft (after it was submitted, or when the user discards it). */
	clear(userId: string, caseFileId: string): void {
		const userDrafts = this.drafts[userId];
		if (!userDrafts?.[caseFileId]) return;
		const remaining: UserDrafts = { ...userDrafts };
		delete remaining[caseFileId];
		const next: DraftMap = { ...this.drafts };
		if (Object.keys(remaining).length === 0) delete next[userId];
		else next[userId] = remaining;
		this.#commit(next);
	}

	/** Deletes every draft for one user (called on logout, and used in tests). */
	clearUser(userId: string): void {
		if (!this.drafts[userId]) return;
		const next: DraftMap = { ...this.drafts };
		delete next[userId];
		this.#commit(next);
	}

	/** Drops every draft and the persisted copy (tests, hard local reset). */
	clearAll(): void {
		this.#commit({});
	}

	#commit(next: DraftMap): void {
		this.drafts = next;
		if (!browser) return;
		try {
			if (Object.keys(next).length === 0) localStorage.removeItem(STORAGE_KEY);
			else localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
		} catch {
			// Storage can be full or blocked (private mode). The in-memory copy
			// still protects the draft for the rest of this page session.
		}
	}
}

export const noteDrafts = new NoteDraftsStore();
